import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "../i18n/LanguageProvider";
import { NAV } from "../constants/testIds";

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), testid: NAV.home, end: true },
    { to: "/about", label: t("nav.about"), testid: NAV.about },
    { to: "/philosophy", label: t("nav.philosophy"), testid: NAV.philosophy },
    { to: "/services", label: t("nav.services"), testid: NAV.services },
    { to: "/team", label: t("nav.team"), testid: NAV.team },
    { to: "/insights", label: t("nav.insights"), testid: "nav-insights" },
    { to: "/contact", label: t("nav.contact"), testid: NAV.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // setScrolled from useState is stable; onScroll is scoped inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid={NAV.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-6">
        <div data-testid={NAV.logo}>
          <Logo size={42} />
        </div>

        <nav className="hidden xl:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-5">
          <Link
            to="/portal"
            className="text-white/70 hover:text-gold flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase"
            data-testid="nav-portal"
          >
            <Lock size={13} strokeWidth={1.4} /> {t("nav.portal")}
          </Link>
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid={NAV.cta}
            className="btn-gold"
          >
            {t("nav.cta")}
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-4">
          <LanguageSwitcher compact />
          <button
            data-testid={NAV.mobileToggle}
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden glass border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/portal" onClick={() => setOpen(false)} className="nav-link flex items-center gap-2">
              <Lock size={13} strokeWidth={1.4} /> {t("nav.portal")}
            </Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="btn-gold w-max">
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
